import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { FormProvider } from '@thinknimble/tn-forms-react'
import { ExpenseForm, ExpenseFormInputs, ExpenseFilter, ExpenseGroup } from 'src/services/expenses'
import { expenseQueries } from 'src/services/expenses/queries'
import { expenseApi } from 'src/services/expenses/api'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'
import { useNavigate } from 'react-router'
import { usePermissions } from 'src/hooks/use-permissions'
import { organizationQueries } from 'src/services/user'
import { useEffect, useMemo, useState } from 'react'
import { SelectOption } from 'src/services/base-model'
import { expenseReportQueries } from 'src/services/expense-reports/queries'
import { expenseSearchQueries } from 'src/services/expense-searches/queries'
import { paymentAccountQueries } from 'src/services/payment-accounts/queries'
import { Button } from 'src/components/button'
import { PageHeader } from 'src/components/layout/page-header'
import { MainContent } from 'src/components/layout/main-content'
import { FilterPane } from 'src/components/layout/filter-pane'
import { ContentPane } from 'src/components/layout/content-pane'
import { AISearchModal, CopyAndSplitModal, ExpensePreviewSidebar } from 'src/components/expenses'
import { Input } from 'src/components/input'
import { useSetAtom } from 'jotai'
import { copiedExpenseAtom } from 'src/stores/atom/expense'
import { usePagination } from 'src/hooks/use-pagination'
import { useAiSearchModes } from 'src/hooks/use-org-feature-config'
import { PaginationComponent } from 'src/components/pagination'
import {
  SparklesIcon,
  ArrowUpTrayIcon,
  DocumentTextIcon,
  PlusIcon,
} from '@heroicons/react/24/outline'

import { CustomSelect } from 'src/components/custom-select'
import { useIsMobile } from 'src/hooks/use-mobile'
import { CurrencyInputField } from 'src/components/currency-input-field'
import { useGlobalFilters } from 'src/hooks/filters/use-global-filters'
import { expenseFiltersAtom, expenseUiFiltersAtom } from 'src/stores/atom/expense-filters'
import { useBulkSelection } from 'src/hooks/use-bulk-selection'
import { BulkActionBar, SelectAllBanner, TriStateCheckbox } from 'src/components/bulk'
import { useToast } from 'src/components/toast'
import { ItemCollection } from 'src/components/layout/item-collection'
import { ListToolbar } from 'src/components/layout/list-toolbar'
import { useViewMode } from 'src/hooks/use-view-mode'
import { ExpenseListItem } from 'src/components/expenses/expense-list-item'

export const ExpensesListView = () => {}

export const ExpensesMainContentView = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { hasPermission } = usePermissions()
  const [selectedExpense, setSelectedExpense] = useState<ExpenseGroup | null>(null)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [viewMode, setViewMode] = useViewMode('expenses', 'list')
  const [isAiSearchModalOpen, setIsAiSearchModalOpen] = useState(false)
  const {
    anyModeEnabled: showAiSearch,
    vectorEnabled: aiVectorEnabled,
    queryBuilderEnabled: aiQueryBuilderEnabled,
  } = useAiSearchModes()
  const [copyAndSplitExpense, setCopyAndSplitExpense] = useState<ExpenseGroup | null>(null)
  const setCopiedExpense = useSetAtom(copiedExpenseAtom)

  const [orgSearch, setOrgSearch] = useState('')
  const [paymentAccountSearch, setPaymentAccountSearch] = useState('')
  const [reportSearch, setReportSearch] = useState('')
  const [expenseSearchSearch, setExpenseSearchSearch] = useState('')

  const {
    pagination: expensePagination,
    paginationCallback,
    goToNextPage,
    goToPrevPage,
    goToPage,
    resetPagination,
  } = usePagination()

  const handleExpenseClick = (expense: ExpenseGroup) => {
    setSelectedExpense(expense)
    setIsSidebarOpen(true)
  }

  const handleCloseSidebar = () => {
    setIsSidebarOpen(false)
    setSelectedExpense(null)
  }

  const handleEditExpense = (id: string) => {
    navigate(`/expenses/expense/${id}`)
    handleCloseSidebar()
  }

  const handleCreateCopy = (id: string) => {
    const expenseToCopy = data?.results.find((e) => e.id === id)
    if (expenseToCopy) {
      // Clear receipts when creating a duplicate
      const { receiptsRef: _receiptsRef, ...expenseWithoutReceipts } = expenseToCopy
      setCopiedExpense({ ...expenseWithoutReceipts, receiptsRef: [] } as ExpenseGroup)
      navigate(`/expenses/expense`)
    }
    handleCloseSidebar()
  }

  const { uiFilters, apiFilters, setFilter, resetFilters } = useGlobalFilters<ExpenseFilter>(
    {
      organization: [],
      category: [],
      cashFlowType: [],
      reports: [],
      paymentAccount: [],
      transactionType: [],
      expenseYear: new Date().getFullYear().toString(),
      search: '',
      isReimbursable: false,
      isReimbursed: false,
      hasReceipts: undefined,
      hasReports: undefined,
      isReconciled: undefined,
      expenseDate: '',
      expenseDate__gte: '',
      expenseDate__lte: '',
      amount: undefined,
      amount__gte: undefined,
      amount__lte: undefined,
      expenseSearch: [],
      includeReimbursableOptions: undefined,
      ordering: '-expense_date',
    },
    expenseFiltersAtom,
    expenseUiFiltersAtom,
  )

  const filterHash = useMemo(() => {
    return JSON.stringify(uiFilters)
  }, [uiFilters])

  const { data, isLoading } = useQuery(
    expenseQueries.list({
      pagination: expensePagination,
      paginationCallback,
      filters: apiFilters,
    }),
  )

  const pageIds = useMemo(() => data?.results.map((e) => e.id) ?? [], [data?.results])
  const totalCount = data?.count ?? 0

  const selection = useBulkSelection<Partial<ExpenseFilter>>({
    pageIds,
    filters: apiFilters,
    totalCount,
  })
  const { reset: resetSelection } = selection

  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false)
  const queryClient = useQueryClient()
  const bulkDeleteMutation = useMutation({
    mutationFn: () => expenseApi.csc.bulkDelete({ payload: selection.payload }),
    onSuccess: (job) => {
      setIsConfirmingDelete(false)
      resetSelection()
      queryClient.invalidateQueries({ queryKey: expenseQueries.all() })
      // Below the threshold the job comes back already done; above it, it runs in
      // the background and the async-jobs tracker toasts on completion.
      toast({
        description:
          job.status === 'done'
            ? t('bulkActions.deleteDone', { count: job.result?.succeeded ?? 0 })
            : t('bulkActions.deleteStarted'),
      })
    },
    onError: () => {
      toast({ description: t('bulkActions.deleteFailed'), type: 'error' })
    },
  })

  const bulkAddToReportMutation = useMutation({
    mutationFn: (reportId: string) =>
      expenseApi.csc.bulkAddToReport({ payload: selection.payload, report: reportId }),
    onSuccess: (job) => {
      resetSelection()
      queryClient.invalidateQueries({ queryKey: expenseQueries.all() })
      // Below the threshold the job comes back already done; above it, it runs in
      // the background and the async-jobs tracker toasts on completion.
      toast({
        description:
          job.status === 'done'
            ? t('bulkActions.addToReportDone', { count: job.result?.attached ?? 0 })
            : t('bulkActions.addToReportStarted'),
      })
    },
    onError: () => {
      toast({ description: t('bulkActions.addToReportFailed'), type: 'error' })
    },
  })

  const bulkCategorizeMutation = useMutation({
    mutationFn: (expenseCategoryId: string) =>
      expenseApi.csc.bulkCategorize({
        payload: selection.payload,
        expenseCategory: expenseCategoryId,
      }),
    onSuccess: (job) => {
      resetSelection()
      queryClient.invalidateQueries({ queryKey: expenseQueries.all() })
      // Below the threshold the job comes back already done; above it, it runs in
      // the background and the async-jobs tracker toasts on completion.
      toast({
        description:
          job.status === 'done'
            ? t('bulkActions.categorizeDone', { count: job.result?.succeeded ?? 0 })
            : t('bulkActions.categorizeStarted'),
      })
    },
    onError: () => {
      toast({ description: t('bulkActions.categorizeFailed'), type: 'error' })
    },
  })

  // Any change to filters, search, or sort order resets both pagination and the
  // selection — a stale filter snapshot must never reach a bulk action.
  useEffect(() => {
    resetPagination()
    resetSelection()
  }, [filterHash, resetPagination, resetSelection])

  const { data: organizations } = useQuery(
    organizationQueries.list({ filters: { search: orgSearch } }),
  )
  const { data: categories } = useQuery(expenseQueries.listCategories())

  // The bulk re-categorize picker is scoped to the selection's country: when the
  // organization filter narrows to a single org, use that org's country so only
  // categories valid for the selection are offered. With no single org selected
  // (mixed/no org filter) the picker falls back to all active categories, and the
  // server still rejects any expense whose country does not match the chosen one.
  const selectionCountry = useMemo(() => {
    const orgIds = uiFilters.organization ?? []
    if (orgIds.length !== 1) return undefined
    const org = organizations?.results.find((o) => o.id === orgIds[0]?.value)
    return org?.country ?? undefined
  }, [uiFilters.organization, organizations?.results])

  const { data: bulkCategories } = useQuery(expenseQueries.listCategories(selectionCountry))
  const { data: cashFlowTypeOpts } = useQuery(expenseQueries.listCashFlowTypeOpts())
  const { data: transactionTypeOpts } = useQuery(expenseQueries.listTransactionTypeOpts())
  const { data: reports } = useQuery({
    ...expenseReportQueries.list({ filters: { search: reportSearch } }),
    enabled: hasPermission('reports.access'),
  })
  const { data: paymentAccounts } = useQuery({
    ...paymentAccountQueries.list({ filters: { search: paymentAccountSearch } }),
    enabled: hasPermission('expenses.access'),
  })
  const { data: expenseSearches } = useQuery(
    expenseSearchQueries.list({ filters: { search: expenseSearchSearch } }),
  )

  const yearOpts = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const years = []
    for (let i = currentYear; i >= 2024; i--) {
      years.push({ label: i.toString(), value: i.toString() })
    }
    return years
  }, [])

  const orgOpts = useMemo(() => {
    return (
      organizations?.results.map((org) => ({
        label: org.name,
        value: org.id,
      })) || []
    )
  }, [organizations?.results])

  const reportOpts = useMemo(() => {
    return (
      reports?.results.map((report) => ({
        label: `${report.name} - ${report.organizationRef?.name}`,
        value: report.id,
      })) || []
    )
  }, [reports?.results])

  const catOpts = useMemo(() => {
    return (
      categories?.map((cat) => ({
        label: cat.label,
        value: cat.id,
      })) || []
    )
  }, [categories])

  // The bulk picker targets the ExpenseCategory FK, so options carry the
  // category id (not the legacy `value` the filter pane's `catOpts` uses).
  const bulkCategoryOpts = useMemo(() => {
    return (
      bulkCategories?.map((cat) => ({
        label: cat.label,
        value: cat.id,
      })) || []
    )
  }, [bulkCategories])

  const cashFlowTypeOptsList = useMemo(() => {
    return (
      cashFlowTypeOpts?.map((opt) => ({
        label: opt[1],
        value: opt[0],
      })) || []
    )
  }, [cashFlowTypeOpts])

  const transactionTypeOptsList = useMemo(() => {
    return (
      transactionTypeOpts?.map((opt) => ({
        label: opt[1],
        value: opt[0],
      })) || []
    )
  }, [transactionTypeOpts])

  const paymentAccountOpts = useMemo(() => {
    return (
      paymentAccounts?.results.map((account) => ({
        label: account.name,
        value: account.id,
      })) || []
    )
  }, [paymentAccounts?.results])

  const expenseSearchOpts = useMemo(() => {
    return (
      expenseSearches?.results.map((search) => {
        const name =
          search.name || search.query.substring(0, 40) + (search.query.length > 40 ? '...' : '')
        return {
          label: name,
          value: search.id,
          searchType: search.searchType,
        }
      }) || []
    )
  }, [expenseSearches?.results])

  const handleAiSearchComplete = (
    _apiFiltersResult: Record<string, unknown>,
    _uiFiltersResult: Record<string, unknown>,
  ) => {
    // TODO: apply the AI-generated filters via useSetAtom(expenseFiltersAtom)
    // and useSetAtom(expenseUiFiltersAtom) once AI search is enabled.
  }

  return (
    <>
      <PageHeader
        title={t('expensesPage.title')}
        actions={
          <>
            <Button
              variant="secondary"
              icon={<DocumentTextIcon />}
              onClick={() => navigate(`/expense-reports`)}
            >
              {t('expensesPage.reportsBtn')}
            </Button>
            <Button
              variant="secondary"
              icon={<ArrowUpTrayIcon />}
              onClick={() => navigate(`/receipts`)}
            >
              {t('expensesPage.uploadReceiptsBtn')}
            </Button>
            {hasPermission('expenses.create') && (
              <Button
                variant="primary"
                icon={<PlusIcon />}
                onClick={() => navigate(`/expenses/expense`)}
              >
                {t('expensesPage.addExpense')}
              </Button>
            )}
          </>
        }
      >
        <Input
          type="text"
          placeholder={t('expensesPage.searchPlaceholder')}
          value={uiFilters.search?.[0]?.value ?? ''}
          onChange={(e) => {
            setFilter('search', [{ label: e.target.value, value: e.target.value }])
          }}
          className="flex-1"
        />
        {showAiSearch && (
          <Button
            variant="ghost"
            onClick={() => setIsAiSearchModalOpen(true)}
            className="flex items-center gap-1 whitespace-nowrap"
            data-testid="ai-search-button"
          >
            <SparklesIcon className="h-5 w-5 text-primary" />
            <span className="hidden sm:inline">{t('expensesPage.aiSearch')}</span>
          </Button>
        )}
      </PageHeader>
      <MainContent>
        <FilterPane
          title={useIsMobile() ? t('common.filters') : ''}
          onClear={() => resetFilters(['search', 'ordering'])}
        >
          <h2 className="mb-2 text-lg font-semibold">{t('expensesPage.sortBy')}</h2>
          <div className="mb-4">
            <select
              className="w-full rounded border p-2"
              data-testid="expense-sort-select"
              value={apiFilters.ordering || '-expense_date'}
              onChange={(e) =>
                setFilter('ordering', [{ label: e.target.value, value: e.target.value }])
              }
            >
              <option value="-expense_date">{t('expensesPage.sortNewestFirst')}</option>
              <option value="expense_date">{t('expensesPage.sortOldestFirst')}</option>
              <option value="-amount">{t('expensesPage.sortAmountHighToLow')}</option>
              <option value="amount">{t('expensesPage.sortAmountLowToHigh')}</option>
            </select>
          </div>

          <h2 className="mb-2 text-lg font-semibold">{t('expensesPage.dateRange')}</h2>
          <div className="mb-4 space-y-2">
            <div>
              <label className="text-sm text-text-secondary">{t('expensesPage.from')}</label>
              <input
                type="date"
                className="w-full rounded border p-2"
                value={apiFilters.expenseDate__gte || ''}
                onChange={(e) =>
                  setFilter('expenseDate__gte', [{ label: e.target.value, value: e.target.value }])
                }
              />
            </div>
            <div>
              <label className="text-sm text-text-secondary">{t('expensesPage.to')}</label>
              <input
                type="date"
                className="w-full rounded border p-2"
                value={apiFilters.expenseDate__lte || ''}
                onChange={(e) =>
                  setFilter('expenseDate__lte', [{ label: e.target.value, value: e.target.value }])
                }
              />
            </div>
          </div>

          <h2 className="mb-2 text-lg font-semibold">{t('expensesPage.amountRange')}</h2>
          <div className="mb-4 space-y-2">
            <div>
              <CurrencyInputField
                label={t('expensesPage.minAmount')}
                value={apiFilters.amount__gte?.toString() || ''}
                currency="USD"
                onValueChange={(val) => {
                  setFilter('amount__gte', val?.float || undefined)
                }}
                onCurrencyChange={() => {}}
                placeholder={t('expensesPage.minAmountPlaceholder')}
              />
            </div>
            <div>
              <CurrencyInputField
                label={t('expensesPage.maxAmount')}
                value={apiFilters.amount__lte?.toString() || ''}
                currency="USD"
                onValueChange={(val) => {
                  setFilter('amount__lte', val?.float || undefined)
                }}
                onCurrencyChange={() => {}}
                placeholder={t('expensesPage.maxAmountPlaceholder')}
              />
            </div>
          </div>

          <h2 className="mb-4 text-lg font-semibold">{t('expensesPage.expenseYear')}</h2>
          <CustomSelect
            searchable={true}
            multi={true}
            clearable={true}
            options={yearOpts}
            values={uiFilters.expenseYear ?? []}
            onChange={(values) => {
              console.log(values)
              setFilter('expenseYear', values)
            }}
          />

          <h2 className="mb-4 text-lg font-semibold">{t('expensesPage.organization')}</h2>
          <CustomSelect
            searchable={true}
            multi={true}
            clearable={true}
            options={orgOpts}
            values={uiFilters.organization ?? []}
            onChange={(values) => setFilter('organization', values)}
            searchFn={({ state }) => {
              setOrgSearch(state.search)
              return orgOpts
            }}
          />

          <h2 className="mb-4 text-lg font-semibold">{t('expensesPage.category')}</h2>
          <CustomSelect
            searchable={true}
            clearable={true}
            multi={true}
            options={catOpts}
            values={uiFilters.category ?? []}
            onChange={(values) => setFilter('category', values)}
          />
          <h2 className="mb-4 text-lg font-semibold">{t('expensesPage.cashFlow')}</h2>
          <CustomSelect
            searchable={true}
            clearable={true}
            multi={true}
            options={cashFlowTypeOptsList}
            values={uiFilters.cashFlowType ?? []}
            onChange={(values) => setFilter('cashFlowType', values)}
          />
          {hasPermission('expenses.access') && (
            <>
              <h2 className="mb-4 text-lg font-semibold">{t('expensesPage.paymentAccount')}</h2>
              <CustomSelect
                searchable={true}
                multi={true}
                clearable={true}
                options={paymentAccountOpts}
                values={uiFilters.paymentAccount ?? []}
                onChange={(values) => setFilter('paymentAccount', values)}
                placeholder={t('expensesPage.paymentAccount')}
                searchFn={({ state }) => {
                  setPaymentAccountSearch(state.search)
                  return paymentAccountOpts
                }}
              />
            </>
          )}
          <h2 className="mb-4 text-lg font-semibold">{t('expensesPage.transactionType')}</h2>
          <CustomSelect
            searchable={true}
            multi={true}
            clearable={true}
            options={transactionTypeOptsList}
            values={uiFilters.transactionType ?? []}
            onChange={(values) => setFilter('transactionType', values)}
            placeholder={t('expensesPage.transactionType')}
          />
          {hasPermission('reports.access') && (
            <>
              <h2 className="mb-4 text-lg font-semibold">{t('expensesPage.report')}</h2>
              <CustomSelect
                searchable={true}
                clearable={true}
                multi={true}
                options={reportOpts}
                values={uiFilters.reports ?? []}
                onChange={(values) => setFilter('reports', values)}
                searchFn={({ state }) => {
                  setReportSearch(state.search)
                  return reportOpts
                }}
              />
            </>
          )}

          <h2 className="mb-4 text-lg font-semibold">{t('expensesPage.savedSearches')}</h2>
          <CustomSelect
            searchable={true}
            clearable={true}
            multi={false}
            options={expenseSearchOpts}
            values={uiFilters.expenseSearch ?? []}
            searchFn={({ state }) => {
              setExpenseSearchSearch(state.search)
              return expenseSearchOpts
            }}
            onChange={(values) => {
              // Set the expenseSearch filter - this sends the ID to the backend
              // For vector searches: backend will use the embedding for similarity search
              // For query_builder: we also apply the saved filters below
              setFilter('expenseSearch', values)

              if (values.length > 0) {
                const selectedSearch = expenseSearches?.results.find(
                  (s) => s.id === values[0].value,
                )

                // For query_builder searches, also apply the saved filters to the UI
                if (selectedSearch?.searchType === 'query_builder' && selectedSearch?.uiFilters) {
                  Object.entries(selectedSearch.uiFilters).forEach(([key, filterData]) => {
                    if (filterData && typeof filterData === 'object' && 'value' in filterData) {
                      const val = (filterData as { value: unknown }).value
                      // For `category` the saved value is the ExpenseCategory id
                      // (it maps to the UUID `expense_category__in` filter); look
                      // the display label up from catOpts so the chip is
                      // selectable/removable and re-applying still sends the id.
                      const labelFor = (v: string) =>
                        key === 'category' ? catOpts.find((o) => o.value === v)?.label ?? v : v
                      const uiValue = Array.isArray(val)
                        ? val.map((v: string) => ({ label: labelFor(v), value: v }))
                        : [{ label: labelFor(String(val)), value: String(val) }]
                      setFilter(key as keyof ExpenseFilter, uiValue as SelectOption[])
                    }
                  })
                }
              }
            }}
            placeholder={t('expensesPage.savedSearchesPlaceholder')}
          />

          <h2 className="mb-2 text-lg font-semibold">{t('expensesPage.reimbursement')}</h2>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={apiFilters.includeReimbursableOptions ?? false}
                onChange={(e) => setFilter('includeReimbursableOptions', e.target.checked)}
              />{' '}
              {t('expensesPage.filterByReimbursement')}
            </label>
            <label className="ml-4 flex items-center gap-2">
              <input
                type="checkbox"
                checked={apiFilters.isReimbursable ?? false}
                disabled={!apiFilters.includeReimbursableOptions}
                onChange={(e) => setFilter('isReimbursable', e.target.checked)}
              />{' '}
              {t('expensesPage.reimbursable')}
            </label>
            <label className="ml-8 flex items-center gap-2">
              <input
                type="checkbox"
                checked={apiFilters.isReimbursed ?? false}
                disabled={!apiFilters.includeReimbursableOptions || !apiFilters.isReimbursable}
                onChange={(e) => setFilter('isReimbursed', e.target.checked)}
              />{' '}
              {t('expensesPage.reimbursed')}
            </label>
          </div>

          <h2 className="mb-2 text-lg font-semibold">{t('expensesPage.reconciled')}</h2>
          <div className="mb-4">
            <select
              className="w-full rounded border p-2"
              data-testid="expense-reconciled-select"
              value={
                apiFilters.isReconciled === true
                  ? 'true'
                  : apiFilters.isReconciled === false
                  ? 'false'
                  : 'any'
              }
              onChange={(e) => {
                const val = e.target.value
                setFilter('isReconciled', val === 'any' ? undefined : val === 'true')
              }}
            >
              <option value="any">{t('expensesPage.reconciledAny')}</option>
              <option value="true">{t('expensesPage.reconciledOnly')}</option>
              <option value="false">{t('expensesPage.unreconciledOnly')}</option>
            </select>
          </div>

          <h2 className="mb-2 mt-4 text-lg font-semibold">{t('expensesPage.statusFilters')}</h2>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="filter-no-receipt"
                checked={apiFilters.hasReceipts === false}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFilter('hasReceipts', false)
                  } else {
                    setFilter('hasReceipts', undefined)
                  }
                }}
              />{' '}
              {t('expensesPage.noReceipt')}
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                data-testid="filter-no-report"
                checked={apiFilters.hasReports === false}
                onChange={(e) => {
                  if (e.target.checked) {
                    setFilter('hasReports', false)
                  } else {
                    setFilter('hasReports', undefined)
                  }
                }}
              />{' '}
              {t('expensesPage.noReport')}
            </label>
          </div>
        </FilterPane>
        <ContentPane>
          <ListToolbar count={data?.count} viewMode={viewMode} onViewModeChange={setViewMode} />
          <section className="gap-y-2">
            {isLoading && <p>{t('common.loading')}</p>}
            {data && data.results.length > 0 && (
              <label className="m-2 flex items-center gap-3 px-6 text-sm text-text-secondary">
                <TriStateCheckbox
                  state={selection.headerState}
                  onChange={selection.togglePage}
                  aria-label={t('bulkActions.selectPage')}
                  data-testid="bulk-select-page"
                />
                {t('bulkActions.selectPage')}
              </label>
            )}
            {data &&
              !selection.isSelectAll &&
              selection.headerState === 'checked' &&
              totalCount > pageIds.length && (
                <SelectAllBanner totalCount={totalCount} onSelectAll={selection.selectAll} />
              )}
            {data && (
              <ItemCollection viewMode={viewMode} className="m-2">
                {data.results.map((expense) => (
                  <ExpenseListItem
                    key={expense.id}
                    expense={expense}
                    viewMode={viewMode}
                    isSelected={selection.isSelected(expense.id)}
                    onToggleSelect={() => selection.toggle(expense.id)}
                    onClick={() => handleExpenseClick(expense)}
                  />
                ))}
              </ItemCollection>
            )}
          </section>
          <PaginationComponent
            pagination={expensePagination}
            isLoading={isLoading}
            onNextPage={goToNextPage}
            onPrevPage={goToPrevPage}
            onGoToPage={goToPage}
            className="mt-4"
          />
        </ContentPane>
      </MainContent>
      {selection.selectedCount > 0 && (
        <BulkActionBar count={selection.selectedCount} onClear={selection.reset}>
          {/* Action controls are delivered by the bulk-delete / bulk-categorize /
              bulk-add-to-report / bulk-download-receipts assertions, which plug
              into this slot and consume `selection.payload`. */}
          <Button
            variant="danger"
            onClick={() => setIsConfirmingDelete(true)}
            data-testid="bulk-action-delete"
          >
            {t('bulkActions.delete')}
          </Button>
          <div className="min-w-56" data-testid="bulk-action-categorize">
            <CustomSelect
              searchable={true}
              options={bulkCategoryOpts}
              values={[]}
              placeholder={t('bulkActions.categorize')}
              disabled={bulkCategorizeMutation.isPending}
              onChange={(values) => {
                const categoryId = values[0]?.value
                if (categoryId) bulkCategorizeMutation.mutate(String(categoryId))
              }}
            />
          </div>
          {hasPermission('reports.access') && (
            <div className="min-w-56" data-testid="bulk-action-add-to-report">
              <CustomSelect
                searchable={true}
                options={reportOpts}
                values={[]}
                placeholder={t('bulkActions.addToReport')}
                disabled={bulkAddToReportMutation.isPending}
                onChange={(values) => {
                  const reportId = values[0]?.value
                  if (reportId) bulkAddToReportMutation.mutate(String(reportId))
                }}
                searchFn={({ state }) => {
                  setReportSearch(state.search)
                  return reportOpts
                }}
              />
            </div>
          )}
          <Button
            variant="ghost"
            onClick={() => toast({ description: t('bulkActions.comingSoon') })}
            data-testid="bulk-action-download-receipts"
          >
            {t('bulkActions.downloadReceipts')}
          </Button>
        </BulkActionBar>
      )}
      <ConfirmDeleteDialog
        isOpen={isConfirmingDelete}
        onConfirm={() => bulkDeleteMutation.mutate()}
        onCancel={() => setIsConfirmingDelete(false)}
        isPending={bulkDeleteMutation.isPending}
        itemName=""
        title={t('bulkActions.confirmDeleteTitle', { count: selection.selectedCount })}
        message={t('bulkActions.confirmDeleteMessage', { count: selection.selectedCount })}
        confirmLabel={t('bulkActions.delete')}
      />
      <ExpensePreviewSidebar
        expense={selectedExpense}
        isOpen={isSidebarOpen}
        onClose={handleCloseSidebar}
        onEdit={handleEditExpense}
        onCreateCopy={handleCreateCopy}
        onCopyAndSplit={(expense) => setCopyAndSplitExpense(expense)}
      />
      <AISearchModal
        isOpen={isAiSearchModalOpen}
        onClose={() => setIsAiSearchModalOpen(false)}
        onSearchComplete={handleAiSearchComplete}
        vectorEnabled={aiVectorEnabled}
        queryBuilderEnabled={aiQueryBuilderEnabled}
      />
      {copyAndSplitExpense && (
        <CopyAndSplitModal
          isOpen={true}
          onClose={() => setCopyAndSplitExpense(null)}
          expense={copyAndSplitExpense}
        />
      )}
    </>
  )
}

export const ExpensesPage = () => {
  return (
    <FormProvider<ExpenseFormInputs> formClass={ExpenseForm}>
      <ExpensesMainContentView></ExpensesMainContentView>
    </FormProvider>
  )
}
